import React, { useEffect, useMemo, useState } from "react";
import { amountFormatter } from "services";
import { DateTime } from "luxon";
import { Button } from "../../../shadcn/components/ui/button";
import MapPin from "../../../icons/MapPin";
import Dot from "../../../icons/Dot";
import RefreshIcon from "../../../icons/RefreshIcon";
import AuctionDisclaimerTag from "../../bidder/Tabs/auctionFloor/components/AuctionDisclaimerTag";
import AuctionStartingSoonCard from "../../bidder/Tabs/auctionFloor/components/AuctionStartingSoonCard";
import BidNotificationPanel from "../../bidder/Tabs/auctionFloor/components/BidNotificationPanel";
import ViewOnlyAccessCard from "../../bidder/Tabs/auctionFloor/components/ViewOnlyAccessCard";
import AssetEventDetails from "./components/AssetEventDetails";
import AssetDetails from "./components/AssetDetails";
import SimpleBidControls from "./components/SimpleBidControls";
import SimpleSwissBidHistory from "./components/SimpleSwissBidHistory";
import BidderRankLabel from "./components/BidderRankLabel";
import BuyerTimerDisplay from "./components/BuyerTimerDisplay";
import {
  currentDummyUser,
  dummyAuctionEvent,
  dummyBidHistory,
  DummyBidEntry,
} from "./dummyBidHallData";

interface BuyerBidHallProps {
  eventData: any;
  bidderName: any;
}

type ConnectionStatus = "Online" | "Offline" | "Connecting";

export default function BuyerBidHall({
  eventData,
  bidderName,
}: BuyerBidHallProps) {
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("Online");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showFailureNotification, setShowFailureNotification] = useState(false);
  const [failureReason, setFailureReason] = useState("");
  const [bidHistory, setBidHistory] =
    useState<DummyBidEntry[]>(dummyBidHistory);

  const resolvedEventData = useMemo(() => {
    if (eventData?.property_dump && eventData?.bidding_increment_amount) {
      return eventData;
    }
    return dummyAuctionEvent;
  }, [eventData]);

  const incrementAmount = Number(
    resolvedEventData?.bidding_increment_amount || 0,
  );
  const reservePrice = Number(resolvedEventData?.reserve_price || 0);

  const [status, setStatus] = useState<any>(
    resolvedEventData?.status || "accepted",
  );
  const [serverNowInMillis, setServerNowInMillis] = useState(
    DateTime.now().setZone("Asia/Kolkata").toMillis(),
  );

  const sortedSuccessfulBids = useMemo(() => {
    return [...bidHistory]
      .filter((bid) => bid.status === "Success")
      .sort(
        (firstBid, secondBid) =>
          firstBid.bid_timestamp - secondBid.bid_timestamp,
      );
  }, [bidHistory]);

  const latestBid = useMemo(() => {
    if (sortedSuccessfulBids.length === 0) {
      return null;
    }
    return sortedSuccessfulBids[sortedSuccessfulBids.length - 1];
  }, [sortedSuccessfulBids]);

  const currentUserLatestBid = useMemo(() => {
    const bidsByCurrentUser = sortedSuccessfulBids.filter(
      (bid) => bid.user_uuid === currentDummyUser.uuid,
    );
    if (bidsByCurrentUser.length === 0) {
      return null;
    }
    return bidsByCurrentUser[bidsByCurrentUser.length - 1];
  }, [sortedSuccessfulBids]);

  const isCurrentUserHighestBidder = Boolean(
    latestBid && latestBid.user_uuid === currentDummyUser.uuid,
  );

  const latestBiddingAmount = latestBid?.bidding_amount || reservePrice;
  const [draftBidAmount, setDraftBidAmount] = useState(latestBiddingAmount);

  const endTimeMillis = useMemo(() => {
    const endTimeAsString = String(resolvedEventData?.final_end_date || "");
    const parsedEndTime = Number(endTimeAsString);
    if (Number.isNaN(parsedEndTime) || parsedEndTime <= 0) {
      return DateTime.now()
        .setZone("Asia/Kolkata")
        .plus({ minutes: 30 })
        .toMillis();
    }
    return parsedEndTime;
  }, [resolvedEventData?.final_end_date]);

  useEffect(() => {
    const timer = setInterval(() => {
      const currentTime = DateTime.now().setZone("Asia/Kolkata").toMillis();
      setServerNowInMillis(currentTime);
      if (currentTime >= endTimeMillis) {
        setStatus("end");
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTimeMillis]);

  useEffect(() => {
    setDraftBidAmount(latestBiddingAmount + incrementAmount);
  }, [latestBiddingAmount, incrementAmount]);

  useEffect(() => {
    if (!showSuccessNotification && !showFailureNotification) {
      return;
    }
    const notificationTimer = setTimeout(() => {
      setShowSuccessNotification(false);
      setShowFailureNotification(false);
      setFailureReason("");
    }, 3200);
    return () => clearTimeout(notificationTimer);
  }, [showSuccessNotification, showFailureNotification]);

  const onRefreshDummyState = () => {
    setBidHistory(dummyBidHistory);
    setStatus(resolvedEventData?.status || "live");
    setConnectionStatus("Online");
    setShowSuccessNotification(false);
    setShowFailureNotification(false);
    setFailureReason("");
  };

  const onIncreaseDraftBid = () => {
    if (status !== "live") {
      return;
    }
    setDraftBidAmount((previousAmount) => previousAmount + incrementAmount);
  };

  const onDecreaseDraftBid = () => {
    if (status !== "live") {
      return;
    }

    const minimumAllowedBid = latestBiddingAmount + incrementAmount;
    setDraftBidAmount((previousAmount) => {
      const nextValue = previousAmount - incrementAmount;
      return Math.max(nextValue, minimumAllowedBid);
    });
  };

  const onPlaceBid = () => {
    if (status !== "live") {
      setShowFailureNotification(true);
      setFailureReason("Auction is not live. Bidding is currently disabled.");
      setShowSuccessNotification(false);
      return;
    }

    if (connectionStatus !== "Online") {
      setShowFailureNotification(true);
      setFailureReason("Connection is unstable. Please try again once online.");
      setShowSuccessNotification(false);
      return;
    }

    if (draftBidAmount <= latestBiddingAmount) {
      setShowFailureNotification(true);
      setFailureReason("Your bid must be higher than the current latest bid.");
      setShowSuccessNotification(false);
      return;
    }

    const nextBidEntry: DummyBidEntry = {
      id: `bid-${Date.now()}`,
      user_uuid: currentDummyUser.uuid,
      bidder_name: String(bidderName || currentDummyUser.name),
      bidding_amount: draftBidAmount,
      bid_timestamp: DateTime.now().setZone("Asia/Kolkata").toMillis(),
      status: "Success",
    };

    setBidHistory((previousHistory) => [...previousHistory, nextBidEntry]);
    setShowSuccessNotification(true);
    setShowFailureNotification(false);
  };

  const millisecondsRemaining = Math.max(endTimeMillis - serverNowInMillis, 0);
  const hoursRemaining = Math.floor(millisecondsRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor(
    (millisecondsRemaining % (1000 * 60 * 60)) / (1000 * 60),
  );
  const secondsRemaining = Math.floor(
    (millisecondsRemaining % (1000 * 60)) / 1000,
  );
  const latestBidTimestampMillis =
    latestBid?.bid_timestamp || serverNowInMillis;
  const latestBidTimestampLabel = DateTime.fromMillis(latestBidTimestampMillis)
    .setZone("Asia/Kolkata")
    .toFormat("dd LLL yyyy, hh:mm:ss a");

  const isSwissAuction = resolvedEventData?.event_type === "Asset transfer";

  return (
    <div className=" bg-white p-2 rounded-sm">
      <div className="w-full mb-2">
        <AuctionStartingSoonCard
          eventData={resolvedEventData}
          status={status}
        />
        <AuctionDisclaimerTag />
      </div>
      <div className="flex flex-col lg:flex-row gap-1 lg:gap-2">
        <div className="w-full bg-white px-2 py-3 border rounded-sm">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="bg-[#FAF7FA] py-1 px-2.5 border border-[#EAE2EA] rounded-full flex items-center">
              <Dot />
              <span className="text-[10px] font-sans pl-2 font-medium text-brand-textBlack tracking-wide">
                ID {resolvedEventData?.listing_id}
              </span>
            </div>
            <Button
              className="h-[30px] bg-white hover:bg-white text-brand-orange hover:border-brand-orange text-xs font-semibold"
              onClick={onRefreshDummyState}
            >
              <div className="flex gap-1 items-center">
                <RefreshIcon />
                <div>Refresh</div>
              </div>
            </Button>
          </div>

          <div className="space-y-1.5 sm:max-w-[320px] sm:ml-auto">
            {showSuccessNotification && (
              <BidNotificationPanel notificationType="bidSuccess" />
            )}
            {showFailureNotification && (
              <BidNotificationPanel
                notificationType="bidFailure"
                reason={failureReason}
              />
            )}
          </div>

          <div className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-brand-textBlack font-sans text-sm lg:text-base font-semibold capitalize">
                  {`${resolvedEventData?.property_dump?.asset?.name} - ${resolvedEventData?.property_dump?.sub_asset_type?.name}`}
                </h3>
                <div className="flex items-start mt-1">
                  <MapPin className="mr-1 w-3 h-3 shrink-0" />
                  <span className="text-[10px] lg:text-xs text-brand-textGrey capitalize">
                    {`${resolvedEventData?.property_dump?.city?.name}, ${resolvedEventData?.property_dump?.state?.name}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-[#FFFCFA] px-2 py-1.5 sm:px-2.5 sm:py-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-sans text-brand-textBlack font-semibold">
                    Latest Bidding Price
                  </div>
                  <p className="max-w-2xl text-sm text-brand-textGrey flex flex-col sm:flex-row sm:items-end gap-0.5 sm:gap-1.5 mt-0.5">
                    <span className="text-lg xl:text-[24px] font-bold text-brand-orange leading-6">
                      ₹ {amountFormatter(latestBiddingAmount)}
                    </span>
                    <span className="text-xs sm:text-sm">
                      at {latestBidTimestampLabel} (IST)
                    </span>
                  </p>
                </div>

                <div className="w-full sm:w-auto shrink-0 border-t border-[#F0EBE8] pt-2 sm:border-t-0 sm:pt-0">
                  <BuyerTimerDisplay
                    hours={hoursRemaining}
                    minutes={minutesRemaining}
                    seconds={secondsRemaining}
                    status={status}
                    endTime={endTimeMillis}
                    extendTimeAt={resolvedEventData?.extend_time_at}
                  />
                </div>
              </div>
            </div>

            <BidderRankLabel isHighestBidder={isCurrentUserHighestBidder} />
            <SimpleSwissBidHistory
              bids={bidHistory}
              currentUserUuid={currentDummyUser.uuid}
              isSwissAuction={isSwissAuction}
            />
            {status === "live" ? (
              <SimpleBidControls
                draftBidAmount={draftBidAmount}
                connectionStatus={connectionStatus}
                onDecreaseDraftBid={onDecreaseDraftBid}
                onIncreaseDraftBid={onIncreaseDraftBid}
                onPlaceBid={onPlaceBid}
              />
            ) : (
              <ViewOnlyAccessCard />
            )}
          </div>
        </div>

        <div className="min-w-[290px] bg-brand-lightOrange rounded-sm">
          <AssetEventDetails
            currentUserLatestBid={currentUserLatestBid}
            isCurrentUserHighestBidder={isCurrentUserHighestBidder}
            latestBiddingAmount={latestBiddingAmount}
            incrementAmount={incrementAmount}
            reservePrice={resolvedEventData.reserve_price}
            biddingIncrementAmount={resolvedEventData.bidding_increment_amount}
            status={status}
            endTimeMillis={endTimeMillis}
          />
          <AssetDetails
            bank={resolvedEventData?.property_dump?.bank?.name}
            state={resolvedEventData?.property_dump?.state?.name}
            city={resolvedEventData?.property_dump?.city?.name}
          />
        </div>
      </div>
    </div>
  );
}
